import { useState } from 'react';
import { App, Modal, Form, DatePicker, Button, Alert, Typography, Space, Checkbox } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { GenerateTransactionsRequest, GenerateTransactionsResponse } from '@mygate/common';
import { transactionQueueService } from '@/services/transactionQueue';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

interface GenerateTransactionsModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface GenerateTransactionsFormValues {
  date: Dayjs;
  dryRun?: boolean;
}

export function GenerateTransactionsModal({ visible, onClose, onSuccess }: GenerateTransactionsModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<GenerateTransactionsFormValues>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GenerateTransactionsResponse | null>(null);

  const handleGenerate = async (values: GenerateTransactionsFormValues) => {
    setLoading(true);
    try {
      const request: GenerateTransactionsRequest = {
        tillDate: values.date.format('YYYY-MM-DD'),
        dryRun: values.dryRun || false,
      };

      const data = await transactionQueueService.generateTransactions(request);
      if (!data) return;

      setResult(data);

      if (data.generated > 0) {
        message.success(`Successfully generated ${data.generated} transactions`);

        // Show summary of results
        let summaryMessage = `Generated: ${data.generated}`;
        if (data.skipped > 0) {
          summaryMessage += `, Skipped: ${data.skipped}`;
        }
        if (data.errors.length > 0) {
          summaryMessage += `, Errors: ${data.errors.length}`;
        }

        message.info(summaryMessage, 5);

        // Reset form and close modal after short delay
        setTimeout(() => {
          form.resetFields();
          setResult(null);
          onSuccess();
          onClose();
        }, 2000);
      } else if (data.errors.length > 0) {
        message.error('Failed to generate transactions. Check the errors below.');
      } else {
        message.warning('No transactions were generated');
      }
    } catch (error) {
      message.error((error instanceof Error && error.message) || 'Failed to generate transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setResult(null);
    onClose();
  };

  const disabledDate = (current: Dayjs) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  return (
    <Modal
      title={
        <Space wrap>
          <CalendarOutlined />
          <span>Generate Transactions</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleGenerate}
        initialValues={{
          date: dayjs(),
          dryRun: false,
        }}
      >
        <Alert
          message="Transaction Generation"
          description={
            <div>
              <p>This will generate transactions from ALL active standing orders for the selected date.</p>
              <ul style={{ marginBottom: 0 }}>
                <li>Only active, non-paused standing orders will be processed</li>
                <li>Transactions for weekends will be moved to the next business day</li>
                <li>Duplicate transactions for the same date will be skipped by default</li>
                <li>Balance checks will be performed unless explicitly disabled</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="date"
          label="Generation Date"
          rules={[{ required: true, message: 'Please select a date' }]}
          help="Select the date for which to generate transactions"
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabledDate={disabledDate} showToday />
        </Form.Item>

        <Form.Item name="dryRun" valuePropName="checked">
          <Checkbox>
            Dry run (preview only)
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              Shows what would be generated without actually creating the transactions
            </Text>
          </Checkbox>
        </Form.Item>

        {result && (
          <Alert
            message="Generation Results"
            description={
              <div>
                <p>
                  <strong>Generated:</strong> {result.generated} transactions
                </p>
                {result.skipped > 0 && (
                  <p>
                    <strong>Skipped:</strong> {result.skipped} (duplicates or weekend adjustments)
                  </p>
                )}
                {result.errors.length > 0 && (
                  <div>
                    <strong>Errors:</strong>
                    <ul style={{ marginTop: 8 }}>
                      {result.errors.map((error, index) => (
                        <li key={index}>
                          Standing Order {error.standingOrderId}: {error.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            }
            type={result.errors.length > 0 ? 'error' : 'success'}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Generate Transactions
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
